import { useState } from 'react';
import {
  Stack, Button, Autocomplete, TextField, MenuItem, Alert, Snackbar, Card, CardContent, Typography, Box, IconButton, Divider, Tooltip,
} from '@mui/material';
import { Add, Layers, Delete } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { verticalsApi } from '../../api/verticals.api';
import { usersApi, type User } from '../../api/users.api';
import { extractApiError } from '../../api/error';

/**
 * Admin: assign users (e.g. Business Heads) to one or more business verticals,
 * and place each user under a reporting Business Head within that vertical
 * (the team graph). A user with NO grants is unrestricted (fail-open).
 */
export function VerticalAccessPage() {
  const qc = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [verticalId, setVerticalId] = useState<number | ''>('');
  const [reportsTo, setReportsTo] = useState<User | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: users } = useQuery({ queryKey: ['users', 'all'], queryFn: () => usersApi.list({ limit: 500 }) });
  const { data: verticals } = useQuery({ queryKey: ['verticals', 'all'], queryFn: () => verticalsApi.listAccessible() });
  const { data: access } = useQuery({ queryKey: ['vertical-access', user?.id], queryFn: () => verticalsApi.listAccess(user!.id), enabled: Boolean(user) });

  const userList = users?.data ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['vertical-access', user?.id] });

  const assignMut = useMutation({
    mutationFn: () => verticalsApi.assignAccess(user!.id, Number(verticalId), reportsTo?.id ?? null),
    onSuccess: () => { setVerticalId(''); setReportsTo(null); setError(null); setToast('Vertical assigned'); refresh(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const revokeMut = useMutation({
    mutationFn: (id: number) => verticalsApi.revokeAccess(id),
    onSuccess: () => { setToast('Access revoked'); refresh(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const reportsToMut = useMutation({
    mutationFn: ({ id, headId }: { id: number; headId: number | null }) => verticalsApi.setReportsTo(id, headId),
    onSuccess: () => { setToast('Reporting head updated'); refresh(); },
    onError: (e) => setError(extractApiError(e)),
  });

  const assignedIds = new Set((access ?? []).map((a) => a.verticalId));
  const available = (verticals ?? []).filter((v) => !assignedIds.has(v.id));

  return (
    <>
      <PageHeader title="Vertical Access & Team" subtitle="Assign users to business verticals and place them under a reporting Business Head. No grants = unrestricted (sees all verticals)." />

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Autocomplete
            options={userList}
            getOptionLabel={(u) => `${u.fullName} (${u.email})`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={user}
            onChange={(_e, v) => { setUser(v); setError(null); }}
            renderInput={(p) => <TextField {...p} label="Select a user" />}
            sx={{ maxWidth: 460 }}
          />
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {user && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom><Layers fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Verticals & reporting for {user.fullName}</Typography>

            <Box sx={{ my: 1.5 }}>
              {(access ?? []).length === 0 ? (
                <Alert severity="info">No vertical grants — this user currently sees <strong>all</strong> verticals (unrestricted).</Alert>
              ) : (
                <Stack divider={<Divider flexItem />}>
                  {(access ?? []).map((a) => (
                    <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, flexWrap: 'wrap' }}>
                      <Typography fontWeight={600} sx={{ minWidth: 180 }}>{a.verticalName}</Typography>
                      <Autocomplete
                        size="small"
                        options={userList.filter((u) => u.id !== a.userId)}
                        getOptionLabel={(u) => u.fullName}
                        isOptionEqualToValue={(x, y) => x.id === y.id}
                        value={userList.find((u) => u.id === a.reportsToUserId) ?? null}
                        onChange={(_e, v) => reportsToMut.mutate({ id: a.id, headId: v?.id ?? null })}
                        renderInput={(p) => <TextField {...p} label="Reports to (Business Head)" />}
                        sx={{ minWidth: 280 }}
                      />
                      <Box sx={{ flexGrow: 1 }} />
                      <Tooltip title="Revoke vertical access">
                        <IconButton color="error" size="small" onClick={() => revokeMut.mutate(a.id)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>Add a vertical</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField select size="small" label="Vertical" value={verticalId} onChange={(e) => setVerticalId(Number(e.target.value))} sx={{ minWidth: 220 }} disabled={available.length === 0}>
                {available.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
              </TextField>
              <Autocomplete
                size="small"
                options={userList.filter((u) => u.id !== user.id)}
                getOptionLabel={(u) => u.fullName}
                isOptionEqualToValue={(x, y) => x.id === y.id}
                value={reportsTo}
                onChange={(_e, v) => setReportsTo(v)}
                renderInput={(p) => <TextField {...p} label="Reports to (optional)" />}
                sx={{ minWidth: 260 }}
              />
              <Button startIcon={<Add />} variant="contained" disabled={!verticalId || assignMut.isPending} onClick={() => assignMut.mutate()}>Assign</Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
